import React from 'react';
import { semaphoreVsBaseline } from '../lib/semaphore';

/** Colori e ordine canonici delle 3 serie usate nei grafici di tendenza/KPI.
 *  `swatch` = colore del quadratino accanto al nome (matcha la "candela"/barra).
 *  `valueColor` = colore del valore numerico (Attuale lo sovrascrive col semaforo). */
export const SERIES_ORDER = [
  { key: 'Attuale',    fallbackKeys: ['actual'],                              swatch: '#0f172a', valueColor: '#0f172a' },
  { key: 'Budget',     fallbackKeys: ['budget'],                              swatch: '#8b5cf6', valueColor: '#8b5cf6' },
  { key: 'Anno Prec.', fallbackKeys: ['last_year', 'Anno Prec', 'anno_prec'], swatch: '#1D4ED8', valueColor: '#1D4ED8' },
];

const SEMAPHORE_HEX = {
  green: '#10b981',   // emerald-500 — matcha la legenda "sopra budget"
  yellow: '#f59e0b',  // amber-500   — matcha la legenda "entro -5%"
  red: '#ef4444',     // red-500     — matcha la legenda "sotto budget"
};

function _findSerie(payload, entry) {
  if (!payload || !Array.isArray(payload)) return null;
  const allKeys = [entry.key, ...(entry.fallbackKeys || [])];
  return payload.find(p => allKeys.includes(p.name) || allKeys.includes(p.dataKey));
}

/** Custom Recharts tooltip content: ordina sempre Attuale → Budget → Anno Prec.,
 *  applica i colori canonici per serie (swatch = colore della candela del grafico)
 *  e usa il semaforo SOLO per il valore di Attuale. Budget e Anno Prec. mantengono
 *  i loro colori canonici nel valore (nero per Budget, blu per Anno Prec.) così
 *  l'utente identifica subito a quale candela corrisponde ogni cifra.
 *  Ignora valori null/undefined; formatta con `fmt`. */
export function SeriesTooltipContent({ active, payload, label, fmt, subtitleKey, periodLabelFmt }) {
  if (!active || !payload || payload.length === 0) return null;
  let header;
  if (typeof periodLabelFmt === 'function') {
    header = periodLabelFmt(label, payload?.[0]?.payload);
  } else {
    header = label;
  }
  const rows = SERIES_ORDER
    .map(entry => {
      const p = _findSerie(payload, entry);
      if (!p) return null;
      if (p.value == null || Number.isNaN(p.value)) return null;
      return { ...entry, value: p.value };
    })
    .filter(Boolean);

  // Solo Attuale viene colorato col semaforo (vs Budget). Budget/Anno Prec.
  // mantengono il colore canonico della loro candela.
  const actual = rows.find(r => r.key === 'Attuale')?.value;
  const budget = rows.find(r => r.key === 'Budget')?.value;
  const actualSemaphoreColor = SEMAPHORE_HEX[semaphoreVsBaseline(actual, budget)] || null;

  const subtitle = subtitleKey && payload?.[0]?.payload ? payload[0].payload[subtitleKey] : null;
  return (
    <div className="bg-white border border-slate-200 shadow-md px-3 py-2 text-xs rounded-none">
      {header && <p className="font-semibold text-slate-900 mb-1">{header}</p>}
      {subtitle && <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">{subtitle}</p>}
      {rows.length === 0 ? (
        <p className="text-slate-400 italic">Nessun valore</p>
      ) : (
        rows.map(r => {
          const valueColor = r.key === 'Attuale' ? (actualSemaphoreColor || r.valueColor) : r.valueColor;
          return (
            <div key={r.key} className="flex items-center gap-2 py-0.5">
              <span className="inline-block w-2.5 h-2.5 flex-shrink-0" style={{ background: r.swatch }} />
              <span className="text-slate-600 min-w-[90px]">{r.key}</span>
              <span className="font-mono font-semibold" style={{ color: valueColor }}>
                {typeof fmt === 'function' ? fmt(r.value) : r.value}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}
